import { css } from '@emotion/react';

import type { NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { Suspense } from 'react';

import CharacterCardList from '@components/characters/CharacterCardList';
import Loading from '@components/common/Loading';
import Tab, { useTab } from '@components/common/Tab';
import PostList from '@components/community/PostList';
import Header from '@components/layout/Header';
import Layout from '@components/layout/Layout';
import SafeArea from '@components/layout/SafeArea';
import StoryList from '@components/stories/StoryList';
import UserInfo from '@components/user/UserInfo';

import { Platform } from '@constants';
import { withAuth } from '@contexts/auth';
import { useGlobalContext } from '@contexts/global';
import { useMe, useScreen } from '@utils/hook';
import { checkRequiredAppVersion } from '@utils/misc';
import { useWebView } from '@utils/webview';

/**
 * Page
 */

type UserTab = 'stories' | 'play' | 'community';

const containerCSS = css`
  padding-bottom: 40px;
`;

const MyPageDetail = () => {
  const { t } = useTranslation();
  const { web } = useWebView();
  const router = useRouter();
  const { platform, version, isCheming } = useGlobalContext();

  const { data: user } = useMe();

  const userId = (user && user.id) || 0;

  useScreen('마이페이지');

  const tabs = useTab<UserTab>(
    [
      { label: t('작품'), value: 'stories' },
      { label: t('플레이'), value: 'play' },
      { label: t('커뮤니티'), value: 'community' },
    ],
    { replace: true }
  );
  const { activeTab } = tabs;

  if (!user) {
    return null;
  }

  const handleNavigateVouchers = () => {
    if (platform === Platform.web) {
      router.push('/vouchers');
    }
    web.openPage('/vouchers');
  };

  const handleNavigateSetting = () => {
    if (platform === Platform.web) {
      router.push('/setting');
    }

    if (
      checkRequiredAppVersion(platform, version, {
        iOS: '2.25.1',
        android: '2.31.1',
      })
    ) {
      web.openPage('/setting');
    } else {
      web.openSetting();
    }
  };

  return (
    <>
      <Header
        title={'마이페이지'}
        left={() => {
          if (isCheming) {
            return <></>;
          }
          return <div onClick={handleNavigateVouchers}>쿠폰함</div>;
        }}
        right={() => <div onClick={handleNavigateSetting}>설정</div>}
        hideBackButton
      />

      <UserInfo userId={userId} isMyPage balloon={true} subscription={true} />

      {!isCheming && <Tab {...tabs} fit />}

      <Suspense fallback={<Loading push />}>
        <div css={containerCSS}>
          {isCheming && <CharacterCardList userId={userId} isMyPage />}
          {activeTab === 'stories' && !isCheming && (
            <StoryList userId={userId} isMyPage />
          )}
          {activeTab === 'play' && !isCheming && (
            <CharacterCardList userId={userId} isMyPage />
          )}
          {activeTab === 'community' && !isCheming && (
            <PostList userId={userId} isMyPage />
          )}
        </div>
      </Suspense>
    </>
  );
};

const MyPage: NextPage = () => {
  return (
    <Layout isPush>
      <Suspense fallback={<Loading center />}>
        <SafeArea bottom>
          <MyPageDetail />
        </SafeArea>
      </Suspense>
    </Layout>
  );
};

export default withAuth(MyPage, { isSignInRequired: true });
